// ENGLISH — courtesy translation. The French version prevails (see fr.js).

import { COURRIEL, TELEPHONE, adresseVendeur, encadreIdentite } from './identite';

const ADRESSE = adresseVendeur('Belgium');
const TRADUCTION =
  'This translation is provided for your convenience. In the event of any discrepancy, the French version is the only one that prevails.';

export default {
  cgv: {
    seoTitre: 'Terms and Conditions of Sale',
    seoDescription:
      'MirumGames terms and conditions: ordering, prices, payment, 14-day right of withdrawal, delivery and warranties.',
    titre: 'Terms and Conditions of Sale',
    maj: 'Last updated: 23/09/2026',
    mentionTraduction: TRADUCTION,
    sections: [
      {
        titre: 'Article 1: General',
        blocs: [
          { p: 'Any order placed on MIRUMGAMES.COM implies prior acceptance of these terms and conditions. The customer acknowledges having read and accepted their content without reservation.' },
        ],
      },
      {
        titre: 'Article 2: Purpose',
        blocs: [
          { p: 'These terms and conditions set out the rights and obligations of the parties involved in the online sale of the products and services offered by MirumGames.com to consumers.' },
        ],
      },
      {
        titre: 'Article 3: Prices and Payment',
        blocs: [
          { p: 'Prices are shown in euros and include applicable VAT. They do not include shipping costs, which are charged separately. Payment must be made in full when the order is confirmed.' },
          { p: 'MirumGames.com accepts card payments through STRIPE, a certified secure payment provider.' },
          { p: "Every paid order gives rise to an invoice in the buyer's name, available on the order confirmation page, through the link in the confirmation email and, for customers with an account, under “My orders”. Stripe also sends a payment receipt by email." },
        ],
      },
      {
        titre: 'Article 4: Right of Withdrawal',
        blocs: [
          { p: 'Private consumers have 14 days to return any product that does not suit them. This period starts on the day of delivery.' },
          { p: 'Return conditions:' },
          {
            liste: [
              'Products must be returned in their original packaging, in perfect resalable condition',
              'Products showing signs of wear will not be accepted',
              'Return shipping costs are borne by the consumer',
              'Refunds are issued by bank transfer within 15 days',
            ],
          },
        ],
      },
      {
        titre: 'Article 5: Commercial Warranty',
        blocs: [
          { p: 'MirumGames.com warrants its products against hidden defects in accordance with Belgian law. MirumGames.com is not the manufacturer of the items offered and accepts no liability for damage caused by a product defect.' },
          { p: "The seller's liability is limited to the exchange or refund of the defective product." },
        ],
      },
      {
        titre: 'Article 6: Product Availability',
        blocs: [
          { p: 'Offers are subject to stock availability. Images, colours, dimensions and weights are provided for information only and are not contractual. MirumGames.com accepts no liability for variations in product characteristics.' },
        ],
      },
      {
        titre: 'Article 7: Delivery',
        blocs: [
          { p: 'Products are delivered to the address given by the consumer on the order form. This must be a physical address (no PO boxes).' },
          { p: 'We ship to Belgium, France, Luxembourg, the Netherlands, Germany and Spain. Average delivery times:' },
          {
            liste: [
              'Luxembourg, Belgium, the Netherlands: 1 to 2 working days',
              'France, Germany: 2 to 4 working days',
              'Spain: 4 to 5 working days',
            ],
          },
          { p: 'Please note: preparation time (1 working day on average) must be added.' },
        ],
      },
      {
        titre: 'Article 8: Conformity of Delivery',
        blocs: [
          { p: 'On receipt, the consumer must check that the products match the order. Any defect must be noted on the delivery slip in handwriting.' },
          { p: 'Any problem (missing or damaged product, etc.) must be reported to MirumGames.com within 96 hours of the shipping notification.' },
        ],
      },
      {
        titre: 'Article 9: Liability',
        blocs: [
          { p: 'MirumGames.com cannot be held liable for damage caused by misuse or improper handling of the products after delivery.' },
        ],
      },
      {
        titre: 'Article 10: Governing Law',
        blocs: [
          { p: 'These terms and conditions are governed by Belgian law. In the event of a dispute, the consumer is invited to contact MirumGames.com first in order to reach an amicable solution.' },
        ],
      },
      {
        titre: 'Legal Notice',
        blocs: [
          { p: 'For the company\'s full legal details (contact information, VAT number, etc.), please see our <a href="/legal" class="text-blue-600 hover:text-blue-800 underline">Legal Notice</a>.' },
          { p: 'Orders whose payment was received up to and including 22/09/2026 (Belgian time) have Charlotte Ansiaux (CBE 1000.107.018) as their seller, and she remains so; orders whose payment is received from 23/09/2026 onwards are sold by Sébastien Schoonjans.' },
        ],
      },
    ],
  },

  confidentialite: {
    seoTitre: 'Privacy Policy',
    seoDescription:
      'MirumGames privacy policy: data collected, how it is used, cookies, anonymous audience measurement and your GDPR rights.',
    titre: 'Privacy Policy',
    maj: 'Last updated: 23/09/2026',
    mentionTraduction: TRADUCTION,
    sections: [
      {
        titre: '1. Personal Data We Collect',
        blocs: [
          { p: 'MirumGames.com collects the personal data needed to:' },
          {
            liste: [
              'Process your order (name, address, email, phone number)',
              'Send you order confirmations and delivery notices',
              'Manage your customer account',
              'Send you our newsletters (with your consent)',
              'Improve our services',
            ],
          },
        ],
      },
      {
        titre: '2. How We Use Your Data',
        blocs: [
          { p: 'Your data is used only for the purposes listed above. MirumGames.com never passes your data to third parties without your explicit consent.' },
          { p: 'The only exceptions are:' },
          {
            liste: [
              'Logistics providers, for delivery',
              'STRIPE, for payment processing',
              'Public authorities, where the law requires it',
            ],
          },
        ],
      },
      {
        titre: '3. Cookies',
        blocs: [
          { p: 'MirumGames.com uses cookies to improve your experience, in particular to:' },
          {
            liste: [
              'Remember your browsing preferences',
              'Manage your shopping basket',
              'Personalise your experience',
            ],
          },
          { p: 'You can disable cookies at any time in your browser settings. This may however affect how the site works.' },
          { p: '<strong>Audience measurement:</strong> we use our own in-house visit counter, with no cookie and no personal data. Neither your IP address nor your browsing history is kept: only aggregated, anonymous statistics are produced (page views, visitors per day). It cannot follow you from one day to the next, nor from one site to another, and you can refuse it at any time through the “Manage my cookies” link at the bottom of the page.' },
        ],
      },
      {
        titre: '4. Your Rights (GDPR)',
        blocs: [
          { p: 'As a resident of Europe, you have the following rights:' },
          {
            liste: [
              '<strong>Right of access:</strong> you may request access to your personal data',
              '<strong>Right to rectification:</strong> you may correct inaccurate data',
              '<strong>Right to erasure:</strong> you may request the deletion of your data',
              '<strong>Right to restriction:</strong> you may restrict the processing of your data',
              '<strong>Right to portability:</strong> you may receive your data in a structured format',
              '<strong>Right to object:</strong> you may object to the processing of your data',
            ],
          },
          { p: `To exercise these rights, contact ${COURRIEL}.` },
        ],
      },
      {
        titre: '5. Data Security',
        blocs: [
          { p: 'MirumGames.com applies appropriate technical and organisational security measures to protect your data:' },
          {
            liste: [
              'SSL/TLS encryption of all connections',
              'Restricted access to personal data',
              'Regular data backups',
              'GDPR compliance',
            ],
          },
        ],
      },
      {
        titre: '6. Retention Period',
        blocs: [
          { p: 'Your personal data is kept for as long as necessary to:' },
          {
            liste: [
              'Fulfil your order and honour warranties',
              'Meet legal obligations (tax obligations in particular)',
              'Manage your customer account',
            ],
          },
          { p: 'After that period, your data is deleted or anonymised.' },
        ],
      },
      {
        titre: '7. Third Parties',
        blocs: [
          { p: 'MirumGames.com may share your data with the following providers:' },
          {
            liste: [
              '<strong>STRIPE</strong>: payment processing',
              '<strong>Sendcloud</strong>: shipping and delivery management',
              '<strong>Sentry</strong>: technical error reporting for the site. Only the information needed to diagnose a fault is transmitted; no recording of your browsing is made.',
              '<strong>Hosting</strong>: storing the site and its data',
            ],
          },
          { p: 'All these providers are required to keep your data confidential.' },
        ],
      },
      {
        titre: '8. Contact',
        blocs: [
          { p: 'For any question regarding your privacy, contact:' },
          { p: `<strong>Data controller:</strong> Sébastien Schoonjans (MirumGames.com)<br /><strong>Email:</strong> ${COURRIEL}<br /><strong>Address:</strong> ${ADRESSE}` },
          { p: 'The data of existing customer accounts was taken over by Sébastien Schoonjans, together with the business of the site, on 23/09/2026.' },
        ],
      },
    ],
  },

  mentions: {
    seoTitre: 'Legal Notice',
    seoDescription:
      'MirumGames legal notice: company information, site owner, VAT number and contact details.',
    titre: 'Legal Notice',
    maj: 'Last updated: 23/09/2026',
    mentionTraduction: TRADUCTION,
    sections: [
      {
        titre: 'Company Information',
        blocs: [
          {
            encadre: encadreIdentite({
              nom: 'Company name',
              statut: 'Status',
              statutValeur: 'self-employed sole trader (natural person)',
              adresse: 'Address',
              pays: 'Belgium',
              bce: 'Company number (CBE)',
              tva: 'VAT number',
              email: 'Email',
              telephone: 'Telephone',
              sep: ':',
            }),
          },
        ],
      },
      {
        titre: 'Publication Director',
        blocs: [
          { p: '<strong>Sébastien Schoonjans</strong><br />Responsible for the publication of MirumGames.com' },
        ],
      },
      {
        titre: 'Hosting',
        blocs: [
          { p: 'MirumGames.com is hosted by:<br /><strong>Cloudflare Pages</strong> (public interface)<br /><strong>Dedicated server</strong> (application and database)<br /><br />Data is hosted in the European Union and processed in accordance with the GDPR.' },
        ],
      },
      {
        titre: 'Intellectual Property',
        blocs: [
          { p: 'All content on MirumGames.com (text, images, illustrations, logos, videos, etc.) is protected by intellectual property law.' },
          { p: 'Any reproduction, representation, modification, publication or adaptation of all or part of the site, by whatever means, is prohibited without the prior written permission of the publisher of the site, Sébastien Schoonjans.' },
        ],
      },
      {
        titre: 'Taxes and Duties',
        blocs: [
          { p: 'Please note that our prices include the taxes and duties applicable in Belgium. Legislation differs from one country to another.' },
          { p: 'Buyers should check whether other taxes or duties are payable in their country of residence and, where applicable, must pay any duties, taxes and charges of any kind relating to the products they import into their country.' },
        ],
      },
      {
        titre: 'Limitation of Liability',
        blocs: [
          { p: 'The publisher of the site endeavours to keep the information published on this site accurate and up to date, and reserves the right to correct its content at any time without notice.' },
          { p: 'The publisher cannot however guarantee the accuracy, precision or completeness of the information made available on this site.' },
        ],
      },
      {
        titre: 'Cookies and Personal Data',
        blocs: [
          { p: 'For any information about the use of cookies and the protection of your personal data, please see our <a href="/privacy" class="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a>.' },
        ],
      },
      {
        titre: 'Governing Law',
        blocs: [
          { p: 'This legal notice is governed by Belgian law. In the event of a dispute, the consumer should first contact MirumGames.com to seek an amicable solution.' },
          { p: 'Failing that, the dispute will be submitted to the exclusive jurisdiction of the competent Belgian courts.' },
        ],
      },
      {
        titre: 'Contact',
        blocs: [
          { p: `For any question about this legal notice, you can contact us at:<br /><strong>Email:</strong> ${COURRIEL}<br /><strong>Address:</strong> ${ADRESSE}<br /><strong>Telephone:</strong> ${TELEPHONE}` },
        ],
      },
    ],
  },

  paiement: {
    seoTitre: 'Payment Security',
    seoDescription:
      'Payment security on MirumGames: transactions handled by Stripe, SSL/TLS encryption, PCI-DSS certification and practical advice.',
    titre: 'Payment Security',
    maj: 'Last updated: 18/02/2024',
    mentionTraduction: TRADUCTION,
    sections: [
      {
        titre: 'Payment through STRIPE',
        blocs: [
          { p: 'Securing card transactions is of the utmost importance. MirumGames.com has chosen to rely on the expertise of STRIPE to guarantee maximum security for your payments.' },
        ],
      },
      {
        titre: 'Is it safe to pay on MirumGames?',
        blocs: [
          { p: 'Yes. During the payment stage, the entire transaction takes place through STRIPE in encrypted mode. You briefly and transparently leave MirumGames.com to enter your card details.' },
          { p: 'This guarantees that:' },
          {
            liste: [
              'MirumGames.com will never know your card number, nor have access to your banking data',
              'MirumGames.com will not know the details of your transactions — only the total amount and the order number are passed to us',
              'Your banking data is handled exclusively by STRIPE, a certified and trusted provider',
            ],
          },
        ],
      },
      {
        titre: 'What security measures does STRIPE use?',
        blocs: [
          { p: 'STRIPE uses the standard encryption system known as the “SSL protocol” (Secure Sockets Layer).' },
          { p: 'When you are on the payment page, check these security indicators:' },
          {
            liste: [
              '<strong>Security icon:</strong> a closed padlock should appear in the corner of your browser',
              '<strong>Address bar:</strong> the URL must start with “https://” (not “http://”)',
              'The “s” in https indicates a secure, encrypted connection',
            ],
          },
          { p: 'If you see a warning message, you are moving from an encrypted to an unencrypted mode (or the reverse). In that case, check before entering any confidential data.' },
        ],
      },
      {
        titre: 'STRIPE certifications',
        blocs: [
          { p: 'STRIPE meets the strictest standards:' },
          {
            liste: [
              '<strong>PCI-DSS</strong> (Payment Card Industry Data Security Standard): the highest security certification for card payments',
              '<strong>GDPR</strong>: full compliance with European regulations',
              '<strong>ISO 27001</strong>: information security management certification',
              '<strong>SOC 2 Type II</strong>: independent security and compliance audit',
            ],
          },
        ],
      },
      {
        titre: 'Security recommendations',
        blocs: [
          { p: 'To keep your payment safe:' },
          {
            liste: [
              '✓ Always use the latest version of your browser',
              '✓ Only pay over secure connections (not on unsecured public WiFi)',
              '✓ Always check that the padlock is closed and that the URL starts with “https://”',
              '✓ Never share your payment details by email or phone',
              '✓ Check your bank statement regularly for any suspicious activity',
            ],
          },
        ],
      },
      {
        titre: 'What happens after payment?',
        blocs: [
          { p: 'Once the payment is approved:' },
          {
            liste: [
              'You receive an order confirmation by email',
              'MirumGames.com receives only the total amount and your order number',
              'STRIPE stores your payment data securely',
              'Your order is prepared and shipped',
            ],
          },
        ],
      },
      {
        titre: 'If something goes wrong',
        blocs: [
          { p: 'If you notice suspicious activity on your account:' },
          {
            liste: [
              'Contact your bank immediately',
              `Send an email to ${COURRIEL} with the details of the problem`,
              'Report the problem to STRIPE directly',
            ],
          },
        ],
      },
      {
        titre: 'Frequently asked questions',
        blocs: [
          { p: '<strong>Q: Does MirumGames.com keep my card details?</strong><br />A: No, absolutely not. STRIPE alone handles your banking data.' },
          { p: `<strong>Q: How can I delete my payment data?</strong><br />A: Contact ${COURRIEL} to request the deletion of your data.` },
          { p: '<strong>Q: Can I use several payment methods?</strong><br />A: Yes. STRIPE accepts credit cards, debit cards and other methods depending on your location.' },
        ],
      },
      {
        titre: 'More information',
        blocs: [
          { p: 'For more detail on STRIPE security, visit: <a href="https://stripe.com/security" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">stripe.com/security</a>' },
        ],
      },
    ],
  },
};
